import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  useTheme,
  Autocomplete,
  TextField,
} from '@mui/material';

const TableHeaderActions = ({
  selectedRows,
  handleClearSelection,
  handleSelectAll,
  data,
  manufacturers,
  selectedManufacturer,
  setSelectedManufacturer,
  types,
  selectedType,
  setSelectedType,
  categories,
  selectedCategory,
  setSelectedCategory,
  facetNames,
  facetOptionsByName,
  selectedFacetFilters,
  setFacetFilter,
  onClearAllFilters,
}) => {
  const theme = useTheme();
  const rootRef = useRef(null);
  const fixedControlsRef = useRef(null);
  const [maxVisibleFacetCount, setMaxVisibleFacetCount] = useState(0);

  const CONTROL_GAP_PX = 6;
  const FACET_CONTROL_WIDTH_PX = 200;
  const CLEAR_BUTTON_WIDTH_PX = 80;
  const hasActiveFilters =
    selectedManufacturer !== 'All' ||
    selectedType !== 'All' ||
    selectedCategory !== 'All' ||
    Object.values(selectedFacetFilters).some(Boolean);

  const baseButtonStyle = {
    fontSize: '0.85rem',
    fontWeight: 600,
    whiteSpace: 'pre',
    minHeight: 34,
    px: 1.25,
  };

  const selectAllButtonStyle = {
    ...baseButtonStyle,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  };

  const clearSelectionButtonStyle = {
    ...baseButtonStyle,
    backgroundColor: theme.palette.tsw.secondary,
    '&:hover': {
      backgroundColor: theme.palette.tsw.secondary,
    },
  };

  const toolbarFieldSx = {
    minWidth: 200,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey[400],
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey[500],
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  };

  useEffect(() => {
    const rootElement = rootRef.current;
    const fixedControlsElement = fixedControlsRef.current;

    if (!rootElement || !fixedControlsElement) {
      return undefined;
    }

    const recalculateVisibleFacetCount = () => {
      const rootWidth = rootElement.getBoundingClientRect().width;
      const fixedWidth = fixedControlsElement.getBoundingClientRect().width;
      const clearButtonWidth = hasActiveFilters ? CLEAR_BUTTON_WIDTH_PX : 0;
      const availableWidth = Math.max(
        0,
        rootWidth - fixedWidth - clearButtonWidth - CONTROL_GAP_PX * 2,
      );
      const slotWidth = FACET_CONTROL_WIDTH_PX + CONTROL_GAP_PX;
      const computedCount = Math.max(0, Math.floor(availableWidth / slotWidth));
      const hasFacetFilters = selectedCategory !== 'All' && selectedCategory !== '' && facetNames.length > 0;
      const nextCount = hasFacetFilters ? Math.max(1, computedCount) : computedCount;
      setMaxVisibleFacetCount(nextCount);
    };

    recalculateVisibleFacetCount();

    const resizeObserver = new ResizeObserver(() => {
      recalculateVisibleFacetCount();
    });

    resizeObserver.observe(rootElement);
    resizeObserver.observe(fixedControlsElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [facetNames.length, hasActiveFilters, selectedCategory]);

  const orderedFacetNames = useMemo(() => {
    const selectedNames = facetNames.filter((name) => Boolean(selectedFacetFilters[name]));
    const unselectedNames = facetNames.filter((name) => !selectedFacetFilters[name]);
    return [...selectedNames, ...unselectedNames];
  }, [facetNames, selectedFacetFilters]);

  const visibleFacetNames = useMemo(() => {
    if (selectedCategory === 'All' || selectedCategory === '') {
      return [];
    }
    return orderedFacetNames.slice(0, maxVisibleFacetCount);
  }, [maxVisibleFacetCount, orderedFacetNames, selectedCategory]);

  return (
    <Box
      ref={rootRef}
      display='flex'
      gap={0.75}
      alignItems='flex-end'
      flexWrap='nowrap'
      sx={{ width: '100%', overflow: 'hidden' }}
    >
      <Box ref={fixedControlsRef} display='flex' gap={0.75} alignItems='flex-end' flexShrink={0}>
        {data.length > 0 && (
          <>
            {selectedRows.length > 0 ? (
              <Button
                variant='contained'
                sx={clearSelectionButtonStyle}
                onClick={handleClearSelection}
              >
                <Typography
                  sx={{ letterSpacing: '0.05em', fontWeight: 'bold', textTransform: 'none' }}
                >
                  Clear Cart
                </Typography>
              </Button>
            ) : (
              <Button variant='contained' sx={selectAllButtonStyle} onClick={handleSelectAll}>
                <Typography
                  sx={{ letterSpacing: '0.05em', fontWeight: 'bold', textTransform: 'none' }}
                >
                  Add All
                </Typography>
              </Button>
            )}
          </>
        )}

        <Autocomplete
          sx={toolbarFieldSx}
          options={manufacturers}
          value={selectedManufacturer === 'All' ? null : selectedManufacturer}
          onChange={(_, newValue) => setSelectedManufacturer(!newValue ? 'All' : newValue)}
          renderInput={(params) => (
            <TextField {...params} size='small' placeholder='Manufacturer' />
          )}
          clearOnBlur={false}
        />
        <Autocomplete
          sx={toolbarFieldSx}
          options={types}
          value={selectedType === 'All' ? null : selectedType}
          onChange={(_, newValue) => setSelectedType(!newValue ? 'All' : newValue)}
          renderInput={(params) => <TextField {...params} size='small' placeholder='Item Type' />}
          clearOnBlur={false}
        />
        <Autocomplete
          sx={toolbarFieldSx}
          options={categories}
          value={selectedCategory === 'All' ? null : selectedCategory}
          onChange={(_, newValue) => setSelectedCategory(!newValue ? 'All' : newValue)}
          renderInput={(params) => <TextField {...params} size='small' placeholder='Category' />}
          clearOnBlur={false}
        />
      </Box>

      <Box display='flex' gap={0.75} alignItems='flex-end' sx={{ minWidth: 0, overflow: 'hidden' }}>
        {visibleFacetNames.map((facetName) => (
          <Autocomplete
            key={facetName}
            sx={toolbarFieldSx}
            options={facetOptionsByName[facetName] || []}
            value={selectedFacetFilters[facetName] || null}
            onChange={(_, newValue) => setFacetFilter(facetName, newValue || '')}
            renderInput={(params) => <TextField {...params} size='small' placeholder={facetName} />}
            clearOnBlur={false}
          />
        ))}
      </Box>

      {hasActiveFilters && (
        <Button
          size='small'
          aria-label='Clear all filters'
          onClick={onClearAllFilters}
          sx={{
            flexShrink: 0,
            minWidth: 'auto',
            px: 0.5,
            color: theme.palette.common.white,
            textTransform: 'none',
            fontSize: '0.72rem',
            lineHeight: 1.05,
            textDecoration: 'underline',
            whiteSpace: 'pre-line',
            textAlign: 'center',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          {'Clear All\nFilters'}
        </Button>
      )}
    </Box>
  );
};

export default TableHeaderActions;
